<template>

  <div>

    <div :id="_uid" class="ui modal" :class="modalSelector">
      <div class="ui header">
        {{ isCreating ? "Novo Modelo de Laudo" : "Alterar Modelo de Laudo" }}
      </div>
      <div class="content">
        <div class="ui form" :class="{ loading }">
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Nome <span class="red text">*</span></label>
              <input class="uppercase" v-model="template.name" @blur="template.name = $toUpperCase($event)">
            </div>     
          </div>
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Título para Impressão <span class="red text">*</span></label>
              <input class="uppercase" v-model="template.title" @blur="template.title = $toUpperCase($event)">
            </div>     
          </div>
          <div class="fields">            
            <div class="sixteen wide field">
              <label class="form-label">Órgãos</label>
              <select-dropdown ref="organsDropdown" :multiple="true" @onAdd="onAddOrgan" @onRemove="onRemoveOrgan" />
            </div>     
          </div>
          <div class="fields">            
            <div class="sixteen wide field">
              <label class="form-label">Observações</label>
              <text-editor ref="commentsEditor" />
            </div>     
          </div>
        </div>
      </div>
      <div class="actions">
        <button class="ui cancel basic button">
          <i class="times icon"></i>
          Cancelar
        </button>      
        <button v-if="isUpdating" class="ui basic red button" 
          :class="{ loading: checkingRemoval || removing, disabled: saving }" @click="onRemove()">
          <i class="trash icon"></i> Excluir
        </button>
        <button class="ui blue button" :class="{ loading: saving, disabled: !canConfirm }" @click="onConfirm()">
          <i class="checkmark icon"></i>
          {{ isCreating ? "Confirmar" : "Atualizar" }}
        </button>
      </div>
    </div>

    <can-not-remove-modal ref="canNotRemoveModal" />

    <yes-no-modal ref="confirmRemovalModal" :message="'Confirma exclusão do modelo ' + template.name + ' ?'" 
      :danger="true" @onYes="onConfirmRemoval()"/>

  </div>
  
</template>

<script>

  import modalDialog from "@/mixins/modal-dialog"
  import CertTemplateService from "@/services/cert-template-service"
  import OrganService from "@/services/organ-service"
  import { SelectDropdown, TextEditor } from "@/components"

  export default {

    name: "CertTemplateFormModal",

    mixins: [modalDialog],

    data() {
      return {
        template: {},
        organs: [],
        service: new CertTemplateService(),
        organService: new OrganService(),
      }
    },

    components: { 
      SelectDropdown,
      TextEditor,
    },

    created() {
      this.loadOrgans()
    },

    computed: {

      canConfirm() {
        
        let canConfirm = true

        if (this.removing || !this.template.name || !this.template.title)
          canConfirm = false

        return canConfirm
      },
    },
    
    methods: {

      async loadOrgans() {

        this.loading = true

        try {

          this.organs = await this.organService.findAll({
            attributes: ["id", "name", "description"],
            order: ["description"],
          })          
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      load(template) {

        this.template = { organs: [] }

        this.$refs.commentsEditor.clear()

        if (template)
          this.template = this.$cloneObject(template)        

        this.loadOrgansDropdown()

        if (this.template.commentsContents)
          this.$refs.commentsEditor.setContents(this.template.commentsContents)

        this.show()
      },

      loadOrgansDropdown() {

        let dropdownValues = []
        const templateOrganIds = this.template.organs.map((organ) => organ.organId)

        this.template.organs.forEach((organ) => {
          dropdownValues.push({
            id: organ.organId,
            description: organ.organ.description,
          })
        })
        
        this.organs.forEach((organ) => {
          if (templateOrganIds.indexOf(organ.id) == -1) {
            dropdownValues.push({
              id: organ.id,
              description: organ.description,
            })
          }
        })

        this.$refs.organsDropdown.setValues(dropdownValues, templateOrganIds)
      },

      onAddOrgan(id) {

        id = parseInt(id)

        const ids = this.template.organs.map((organ) => organ.organId)

        if (ids.indexOf(id) == -1) {
          this.template.organs.push({ 
            organId: id, 
            sequence: (this.template.organs.length + 1) 
          })
        }  
      },

      onRemoveOrgan(id) {
        
        let index = 0

        for (let organ of this.template.organs) {          
          if (organ.organId == id)
            break
          index++  
        }

        this.template.organs.splice(index, 1)
      },

      async onConfirm() {

        if (this.saving)
          return

        this.saving = true
        
        try {
          
          if (!this.$refs.commentsEditor.isEmpty()) {
            this.template.comments = this.$refs.commentsEditor.getText()
            this.template.commentsContents = this.$refs.commentsEditor.getContents({ toString: true })
          }  

          if (this.isCreating) {
            await this.service.create(this.template)
          } else {            
            await this.service.update(this.template)            
          }

          this.emit("onSave", this.template)

          this.$msg.success(this.isCreating ? "Modelo criado com sucesso" : "Modelo alterado com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.saving = false
        }
      },

      async onRemove() {

        this.checkingRemoval = true

        try {

          const data = await this.service.canRemove(this.template)

          if (data.canRemove)  {
            this.$refs.confirmRemovalModal.show()              
          } else {
            this.$refs.canNotRemoveModal.show(data.entities)            
          }

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.checkingRemoval = false
        }        
      },

      async onConfirmRemoval() {

        if (this.removing)
          return

        this.removing = true

        try {

          await this.service.remove(this.template) 

          this.emit("onRemove", this.template)

          this.$msg.success("Modelo excluído com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.removing = false
        }
      },
    },
  }

</script>
